/**
 * "Set Seating" — where the host says who sits at East, South, West and North
 * for a game started with "Decide with the dice" (see `src/game/seating.ts`).
 * Reached from the play screen; also reused to correct the seating while no
 * round has been recorded yet.
 *
 * Nothing here is checked against the dice roll: the roll never touches the
 * game (see `diceRoller.ts`), so this is simply where the players sit. A wind
 * already given to one player is greyed out for the others, so the same seat
 * can't be picked twice, and Confirm stays disabled until all four are set.
 */

import { SEAT_WINDS, seatingProblem, type SeatAssignment } from '../../game/seating';
import type { Game } from '../../game/types';
import type { Wind } from '../../rules/tiles';
import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';

export interface SeatingActions {
  confirm(assignment: SeatAssignment): void;
  cancel(): void;
}

const WIND_LABEL: Record<Wind, string> = {
  east: 'East (deals first)',
  south: 'South',
  west: 'West',
  north: 'North',
};

export function renderSetSeating(root: HTMLElement, game: Game, actions: SeatingActions): void {
  // Correcting an earlier seating starts from it; a fresh one starts empty.
  const alreadySet = game.seatingPending !== true;

  const rows = game.players
    .map(
      (p, i) => `
      <div class="seat-row">
        <label class="seat-name" for="seat-${i}">${esc(p.name)}</label>
        <select id="seat-${i}" data-player-id="${esc(p.id)}">
          <option value="">Choose seat…</option>
          ${SEAT_WINDS.map(
            (w) => `<option value="${w}" ${alreadySet && p.seat === w ? 'selected' : ''}>${WIND_LABEL[w]}</option>`,
          ).join('')}
        </select>
      </div>`,
    )
    .join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Set Seating</h1>
      <p class="muted">
        After everyone has rolled the dice, choose where each player sits. Nothing is checked against
        the roll — this is just who sits at which wind. East deals first.
      </p>
      <div>${rows}</div>
      <p id="seatingHint" class="muted" style="margin:0.5rem 0 0"></p>
      <button type="button" id="confirmSeatingBtn" style="width:100%;margin-top:1rem" disabled>Confirm Seating</button>
      <button type="button" id="cancelSeatingBtn" class="ghost" style="width:100%;margin-top:0.5rem">← Back</button>
    </div>
  `;

  const selects = [...root.querySelectorAll<HTMLSelectElement>('select[data-player-id]')];
  const hint = root.querySelector<HTMLParagraphElement>('#seatingHint')!;
  const confirmBtn = root.querySelector<HTMLButtonElement>('#confirmSeatingBtn')!;

  const read = (): SeatAssignment => {
    const assignment: Record<string, Wind> = {};
    for (const select of selects) {
      if (select.value) assignment[select.dataset['playerId']!] = select.value as Wind;
    }
    return assignment;
  };

  function refresh(): void {
    const assignment = read();
    // A seat someone else already has can't be chosen again.
    for (const select of selects) {
      const mine = select.value;
      for (const option of select.querySelectorAll<HTMLOptionElement>('option')) {
        if (!option.value) continue;
        const takenByOther = selects.some((other) => other !== select && other.value === option.value);
        option.disabled = takenByOther && option.value !== mine;
      }
    }
    const problem = seatingProblem(game, assignment);
    hint.textContent = problem ?? 'Ready — tap Confirm Seating.';
    confirmBtn.disabled = problem !== undefined;
  }

  for (const select of selects) select.addEventListener('change', refresh);
  refresh();

  confirmBtn.addEventListener('click', () => {
    const assignment = read();
    if (seatingProblem(game, assignment)) return;
    actions.confirm(assignment);
  });
  root.querySelector<HTMLButtonElement>('#cancelSeatingBtn')!.addEventListener('click', () => actions.cancel());
}
